import { baseApiService } from './base'
import type {
  TasksResponse,
  Task,
  TaskFilter,
  TaskApprovalRequest,
  TaskRejectionRequest,
  TaskReassignRequest,
  TaskCancelRequest,
  EffectiveDateRequest,
  ESignature
} from './types'

/**
 * Get tasks for current user - uses /api/tasks endpoint (not v2)
 * Matches legacy api.tasks.getMyTasks() behavior
 *
 * @param params - Optional params object with taskStatus, workflowId, userId
 *                 OR legacy string userId for backward compatibility
 */
export async function getMyTasks(
  params?:
    | {
        sortBy?: string
        sortDesc?: boolean
        pageSize?: number
        pageStart?: number
        taskStatus?: string
        workflowId?: number
        userId?: number | string
      }
    | string
): Promise<TasksResponse> {
  // Handle legacy call signature where just userId was passed as string
  let queryParams: { taskStatus?: string; workflowId?: number; userId?: number | string } = {}

  if (typeof params === 'string') {
    // Legacy: getMyTasks(userId)
    queryParams = { userId: params }
  } else if (params) {
    // New: getMyTasks({ taskStatus, workflowId, userId })
    queryParams = params
  }

  return baseApiService.fetch<TasksResponse>('/api/tasks', {
    params: queryParams
  })
}

export async function getTasksList(params: TaskFilter): Promise<TasksResponse> {
  return baseApiService.fetch<TasksResponse>('/api/v2/tasks', {
    params
  })
}

export async function getTask(taskId: string): Promise<Task> {
  return baseApiService.fetch<Task>(`/api/tasks/${taskId}`)
}

export async function approveTask(
  taskId: string,
  comment?: string,
  esignature?: ESignature,
  latestDocModifiedTs?: number,
  skipStatusForDocs?: boolean
): Promise<Task> {
  // Match legacy behavior: only include properties if they have values
  const request: TaskApprovalRequest = {
    comment,
    status: 'APPROVED'
  }

  // Only add esignature if provided (matches legacy: if (validation) { reqObj.esignature = validation; })
  if (esignature) {
    request.esignature = esignature
  }

  // Only add latestDocModifiedTs if provided (matches legacy: if (latestDocModifiedTs) { ... })
  if (latestDocModifiedTs) {
    request.latestDocModifiedTs = latestDocModifiedTs
  }

  // Only add skipStatusForDocs if not undefined/null (matches legacy check)
  if (skipStatusForDocs !== undefined && skipStatusForDocs !== null) {
    request.skipStatusForDocs = skipStatusForDocs
  }

  return baseApiService.fetch<Task>(`/api/tasks/${taskId}`, {
    method: 'PATCH',
    body: request
  })
}

export async function rejectTask(
  taskId: string,
  comment?: string,
  customRejectReasonText?: string,
  rejectReasonId?: number,
  esignature?: ESignature
): Promise<Task> {
  const request: TaskRejectionRequest = {
    comment,
    customRejectReasonText,
    rejectReasonId,
    status: 'REJECTED',
    esignature
  }

  return baseApiService.fetch<Task>(`/api/tasks/${taskId}`, {
    method: 'PATCH',
    body: request
  })
}

export async function reassignTask(
  taskId: string,
  userId: number,
  comment?: string,
  esignature?: ESignature
): Promise<Task> {
  const request: TaskReassignRequest = {
    userId,
    comment,
    esignature
  }

  return baseApiService.fetch<Task>(`/api/tasks/${taskId}/reassign`, {
    method: 'PATCH',
    body: request
  })
}

export async function cancelTask(taskId: string, comment?: string, esignature?: ESignature): Promise<Task> {
  const request: TaskCancelRequest = {
    comment,
    status: 'CANCELLED',
    esignature
  }

  return baseApiService.fetch<Task>(`/api/tasks/${taskId}`, {
    method: 'PATCH',
    body: request
  })
}

export async function cancelRATask(taskId: string, esignature?: ESignature): Promise<Task> {
  return baseApiService.fetch<Task>(`/api/tasks/${taskId}/cancelratask`, {
    method: 'PATCH',
    body: esignature || {}
  })
}

export async function changeEffectiveDate(
  taskId: string,
  newDate?: number,
  makeEffectiveImmediately?: boolean,
  esignature?: ESignature
): Promise<Task> {
  const request: EffectiveDateRequest = {
    effectiveDate: makeEffectiveImmediately ? null : newDate,
    makeEffectiveImmediately: makeEffectiveImmediately || false,
    esignature
  }

  return baseApiService.fetch<Task>(`/api/tasks/${taskId}/effectivedate`, {
    method: 'PATCH',
    body: request
  })
}

export async function findTaskForFile(fileId: number): Promise<Task | null> {
  return baseApiService.fetch<Task | null>(`/api/tasks/findTaskForFile/${fileId}`)
}

export async function findAcknowledgeTaskForFile(
  fileId: number,
  versionId?: number,
  minorVersionId?: number
): Promise<Task | null> {
  const params: Record<string, number> = {}
  if (versionId !== undefined && versionId >= 0) {
    params.versionId = versionId
    if (minorVersionId !== undefined) {
      params.minorVersionId = minorVersionId
    }
  }

  return baseApiService.fetch<Task | null>(`/api/tasks/findAcknowledgeTaskForFile/${fileId}`, {
    params
  })
}

export async function findEditTaskForFile(fileId: number): Promise<Task | null> {
  return baseApiService.fetch<Task | null>(`/api/tasks/findEditTaskForFile/${fileId}`)
}

export async function findTaskRequiringViewingOfFile(
  fileId: number,
  versionId?: number,
  minorVersionId?: number
): Promise<Task | null> {
  const params: Record<string, number> = {}
  if (versionId !== undefined && versionId >= 0) {
    params.versionId = versionId
    if (minorVersionId !== undefined) {
      params.minorVersionId = minorVersionId
    }
  }

  return baseApiService.fetch<Task | null>(`/api/tasks/findTaskRequiringViewingOfFile/${fileId}`, {
    params
  })
}

export async function onedriveWfHasAccess(workflowId: number): Promise<boolean> {
  return baseApiService.fetch<boolean>(`/api/onedrive/workflow/${workflowId}/hasAccess`)
}

export async function exportTaskReports(
  filter: Record<string, unknown>,
  meta: Record<string, unknown>
): Promise<unknown> {
  return baseApiService.fetch('/api/reports/taskexport', {
    method: 'POST',
    body: { filter, meta }
  })
}

// Export tasks API object for AngularJS compatibility
export const tasks = {
  getList: getTasksList,
  getTasksList,
  getMyTasks,
  getTask,
  approveTask,
  rejectTask,
  reassignTask,
  cancelTask,
  cancelRATask,
  changeEffectiveDate,
  findTaskForFile,
  findAcknowledgeTaskForFile,
  findEditTaskForFile,
  findTaskRequiringViewingOfFile,
  onedriveWfHasAccess,
  exportTaskReports
}
